import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getBoolean,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { ProjectName, ProjectNameDTO } from "../types/projectName";
import { SurveyDetailsAndQuestions, SurveyDetailsAndQuestionsDTO } from "../types/surveyDetailsAndQuestions";
/* eslint-disable import/export */
export abstract class GetQuestionsOfSurveyRPC implements
  LeoRPC<GetQuestionsOfSurveyRPC.Request, GetQuestionsOfSurveyRPC.Response, GetQuestionsOfSurveyRPC.Errors.Errors>
{
  abstract execute(
    request: GetQuestionsOfSurveyRPC.Request
  ): Promise<LeoRPCResult<GetQuestionsOfSurveyRPC.Response, GetQuestionsOfSurveyRPC.Errors.Errors>>;
}

export namespace GetQuestionsOfSurveyRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetQuestionsOfSurveyRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
  
  
    constructor(
    surveyId: LeoUUID, 
    projectId: LeoUUID
    ) {
      this.surveyId = surveyId;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        surveyId,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetQuestionsOfSurveyRPC(surveyId='${this.surveyId}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    projectName: ProjectNameDTO;
    surveyDetailsAndQuestions: SurveyDetailsAndQuestionsDTO;
    doesSurveyContainRules: boolean;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetQuestionsOfSurveyResponse: boolean = true;
  
    readonly projectName: ProjectName;
    readonly surveyDetailsAndQuestions: SurveyDetailsAndQuestions;
    readonly doesSurveyContainRules: boolean;
  
  
    constructor(
    projectName: ProjectName, 
    surveyDetailsAndQuestions: SurveyDetailsAndQuestions, 
    doesSurveyContainRules: boolean
    ) {
      this.projectName = projectName;
      this.surveyDetailsAndQuestions = surveyDetailsAndQuestions;
      this.doesSurveyContainRules = doesSurveyContainRules;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const projectName = ProjectName.fromDTO(getObject(dto, "projectName", InvalidResponseProjectNameError));
      const surveyDetailsAndQuestions = SurveyDetailsAndQuestions.fromDTO(getObject(dto, "surveyDetailsAndQuestions", InvalidResponseSurveyDetailsAndQuestionsError));
      const doesSurveyContainRules = getBoolean(dto, "doesSurveyContainRules", InvalidResponseDoesSurveyContainRulesError);
      return new Response(
        projectName,
        surveyDetailsAndQuestions,
        doesSurveyContainRules
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          projectName: this.projectName.toDTO(),
          surveyDetailsAndQuestions: this.surveyDetailsAndQuestions.toDTO(),
          doesSurveyContainRules: this.doesSurveyContainRules
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetQuestionsOfSurvey(projectName='${this.projectName}',surveyDetailsAndQuestions='${this.surveyDetailsAndQuestions}',doesSurveyContainRules='${this.doesSurveyContainRules}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetQuestionsOfSurveyRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetQuestionsOfSurveyRPC.INVALID_PROJECT_ID()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidProjectId;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidProjectIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends GetQuestionsOfSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends GetQuestionsOfSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseProjectNameError extends GetQuestionsOfSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectNameError";
  }
}

export class InvalidResponseSurveyDetailsAndQuestionsError extends GetQuestionsOfSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyDetailsAndQuestionsError";
  }
}

export class InvalidResponseDoesSurveyContainRulesError extends GetQuestionsOfSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDoesSurveyContainRulesError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetQuestionsOfSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetQuestionsOfSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

